import pyodideManifest from "../../scripts/pyodide-assets-manifest.json";
import {
    PYODIDE_BUILTIN_IMPORT_ROOTS,
    PYODIDE_LOCKED_PACKAGE_NAME_OVERRIDES,
    PYODIDE_STDLIB_IMPORT_ROOTS,
} from "./pyodideImportCatalog.js";
import {
    pythonPackageReferenceRoots,
} from "./pythonPackageReferences.js";
export {
    pythonImportRoots,
    pythonPackageReferenceRoots,
} from "./pythonPackageReferences.js";

export const KERNEL_SHARE_WARNING_CODE = Object.freeze({
    PYTHON_PACKAGE_NOT_CURATED: "python-package-not-curated",
    STORED_TOOL_INERT: "stored-tool-inert",
});

const CURATED_PACKAGE_NAMES = new Set(
    pyodideManifest.curatedPackages.packages.map((record) => record.name),
);
const ON_DEMAND_PACKAGE_NAMES = new Set(
    pyodideManifest.onDemandPackages.roots,
);

const PYODIDE_BASE_IMPORT_ROOTS = new Set([
    ...PYODIDE_STDLIB_IMPORT_ROOTS,
    ...PYODIDE_BUILTIN_IMPORT_ROOTS,
]);

/**
 * Saving is allowed, but a stored definition loses tool and on-demand wheel
 * authority. Return precise warnings so every save surface can say that out
 * loud without turning this authoring aid into a security boundary.
 */
export function kernelShareWarnings(kernel) {
    if (!Array.isArray(kernel?.steps)) return [];
    const warnings = [];

    kernel.steps.forEach((step, stepIndex) => {
        const stepId = typeof step?.id === "string" && step.id
            ? step.id
            : null;
        if (step?.kind === "mcp_tool") {
            warnings.push(Object.freeze({
                code: KERNEL_SHARE_WARNING_CODE.STORED_TOOL_INERT,
                stepId,
                stepIndex,
                server: typeof step.server === "string" ? step.server : null,
                tool: typeof step.tool === "string" ? step.tool : null,
            }));
            return;
        }
        if (step?.kind !== "python" || typeof step.code !== "string") return;

        const packageNames = new Set();
        for (const packageName of step.packages || []) {
            if (ON_DEMAND_PACKAGE_NAMES.has(packageName)) {
                packageNames.add(packageName);
            }
        }
        for (const importRoot of pythonPackageReferenceRoots(step.code)) {
            if (PYODIDE_BASE_IMPORT_ROOTS.has(importRoot)) continue;
            const packageName = PYODIDE_LOCKED_PACKAGE_NAME_OVERRIDES[importRoot]
                || importRoot;
            if (!CURATED_PACKAGE_NAMES.has(packageName)) {
                packageNames.add(packageName);
            }
        }
        if (packageNames.size) {
            warnings.push(Object.freeze({
                code: KERNEL_SHARE_WARNING_CODE.PYTHON_PACKAGE_NOT_CURATED,
                stepId,
                stepIndex,
                packageNames: Object.freeze([...packageNames].sort()),
            }));
        }
    });

    return warnings;
}

export function formatKernelShareWarnings(warnings) {
    if (!Array.isArray(warnings) || warnings.length === 0) return null;
    const details = warnings.map((warning) => {
        const step = warning.stepId
            ? ` "${warning.stepId}"`
            : ` ${Number(warning.stepIndex ?? 0) + 1}`;
        if (warning.code === KERNEL_SHARE_WARNING_CODE.STORED_TOOL_INERT) {
            const tool = [warning.server, warning.tool].filter(Boolean).join(" / ");
            return `tool step${step}${tool ? ` (${tool})` : ""} will not run after save`;
        }
        const packages = warning.packageNames?.join(", ") || "a package outside the shared set";
        return `Python step${step} imports ${packages}, which shared runs cannot install`;
    });
    return `Saved with sharing warning${details.length === 1 ? "" : "s"}: ${details.join("; ")}.`;
}
